import styled from '@benzinga/themetron';
import { formatPriceWithCurrency } from '@benzinga/utils/numberUtils';
import type { QuoteSessionType, DelayedQuote } from '@benzinga/quotes-manager';
import { REGULAR } from '@benzinga/quotes-manager/constants';
import { Up } from '@benzinga/themed-icons';
import { QuoteChangePercent } from './QuoteChangePercent';
import { QuoteChange } from './QuoteChange';
import { getTimeLabels, isExtendedHours, extendedPriceData } from '../utils';

export const UpArrow = <Up className="fill-current mr-1" height="0.8em" viewBox="0 0 24 24" width="0.8em" />;

export const DownArrow = (
  <Up className="rotate-180 fill-current mr-1" height="0.8em" viewBox="0 0 24 24" width="0.8em" />
);

export interface QuotePriceProps {
  quote: DelayedQuote;
  sessionType?: QuoteSessionType;
  lastUpdateClassName?: string;
  showDelayedText?: boolean;
}

export const getArrow = (change: number) => {
  if (change < 0) {
    return DownArrow;
  } else if (change > 0) {
    return UpArrow;
  }
  return null;
};

// Remove showDelayedProp when we want to reenable
export const QuotePriceFull = ({
  lastUpdateClassName,
  quote,
  sessionType,
  showDelayed = false,
  showDelayedText = true,
}: QuotePriceProps & { showDelayed?: boolean }) => {
  // const showDelayed = false; //sessionType !== NO_TRADING;
  return (
    <div className="quote-price-full-wrapper">
      <QuotePriceFullContainer className="quote-price-full-container">
        <QuotePrice
          lastUpdateClassName={lastUpdateClassName}
          quote={quote}
          sessionType={sessionType}
          showDelayedText={showDelayedText}
        />
        <div className="flex flex-col secondary-quote-container">
          {sessionType !== REGULAR && (
            <QuotePriceExtended
              lastUpdateClassName={lastUpdateClassName}
              quote={quote}
              sessionType={sessionType}
              showDelayedText={showDelayedText}
            />
          )}
        </div>
      </QuotePriceFullContainer>
      {showDelayed && (
        <div className="whitespace-nowrap text-xs">
          <i>15 minutes delayed</i>
        </div>
      )}
    </div>
  );
};

const QuotePriceFullContainer = styled.div`
  display: flex;
  align-items: baseline;
  flex-direction: column;
  gap: 8px;
`;

export const QuotePrice = ({ lastUpdateClassName, quote, sessionType, showDelayedText }: QuotePriceProps) => {
  const labels = getTimeLabels(quote, sessionType);
  const isCrypto = quote?.type === 'CRYPTO';

  const closeChange =
    typeof quote?.close === 'number' && typeof quote?.previousClosePrice === 'number'
      ? quote?.close - quote?.previousClosePrice
      : null;

  const closeChangePercent =
    closeChange && quote?.previousClosePrice ? (closeChange / quote.previousClosePrice) * 100 : null;

  return (
    <QuotePriceContainer
      absoluteChange={isCrypto ? quote.change : closeChange}
      className="primary"
      lastUpdateClassName={lastUpdateClassName}
      percentageChange={isCrypto ? quote.changePercent : closeChangePercent}
      price={isCrypto ? quote.lastTradePrice : quote.close}
      quote={quote}
      sessionType={sessionType}
      showDelayedText={showDelayedText}
      timeLabel={labels.timestampClose}
    />
  );
};

export const QuotePriceExtended = ({ lastUpdateClassName, quote, sessionType, showDelayedText }: QuotePriceProps) => {
  const labels = getTimeLabels(quote, sessionType);
  const extendedData = extendedPriceData(quote);
  if (!extendedData) return null;
  return (
    <QuotePriceContainer
      absoluteChange={extendedData.change}
      lastUpdateClassName={lastUpdateClassName}
      percentageChange={extendedData.changePercent}
      price={extendedData.price}
      quote={quote}
      sessionType={sessionType}
      showDelayedText={showDelayedText}
      timeLabel={labels.timestampEth}
    />
  );
};

export interface QuotePriceContainerProps {
  absoluteChange: number | null;
  className?: string;
  currency?: string;
  percentageChange: number | null;
  price: number;
  sessionType?: QuoteSessionType;
  timeLabel: string;
  quote?: DelayedQuote;
  lastUpdateClassName?: string;
  showDelayedText?: boolean;
}

export const QuotePriceContainer = ({
  absoluteChange,
  className,
  currency,
  lastUpdateClassName,
  percentageChange,
  price,
  quote,
  sessionType,
  showDelayedText = true,
  timeLabel,
}: // time,
QuotePriceContainerProps) => {
  const arrow = getArrow(absoluteChange ?? 0);
  const isExtended = sessionType ? isExtendedHours(sessionType) : false;
  return (
    <QuotePriceWrapper className={className}>
      <div className="quote-price-data">
        {typeof price === 'number' && <div className="quote-price">{formatPriceWithCurrency(price, currency)}</div>}
        <div className="quote-change-data">
          {arrow}
          {typeof absoluteChange === 'number' && (
            <span className="mr-1">
              <QuoteChange absoluteChange={absoluteChange} />
            </span>
          )}
          {typeof percentageChange === 'number' && (
            <QuoteChangePercent percentageChange={percentageChange} showBrackets={true} />
          )}
        </div>
      </div>
      {timeLabel && (
        <div className={`last-update-wrapper text-bzgray-600 text-xs ${lastUpdateClassName ?? ''}`}>
          <span className={`timelabel`}>{timeLabel}</span>
          {!isExtended && quote?.lastTradeTime && (
            <span>
              {showDelayedText && (
                <>
                  {' '}
                  (Delayed 15-Minutes)
                  <br />
                </>
              )}
              <a
                data-action="Quote Page Get Real Time with PRO"
                href="https://pro.benzinga.com/?utm_source=quotes-page"
                rel="noreferrer"
                target="_blank"
              >
                Get Real Time Here
              </a>
            </span>
          )}
        </div>
      )}
    </QuotePriceWrapper>
  );
};

const QuotePriceWrapper = styled.div`
  .quote-price-data,
  .quote-change-data {
    align-items: baseline;
    display: flex;
  }
  .quote-price-data {
    flex-wrap: wrap;
    flex-direction: column;
  }
  .quote-price {
    font-size: 1.2rem;
    font-weight: ${({ theme }) => theme.fontWeight.semibold};
    margin-right: 0.4rem;
    color: #000000;
  }
  &.primary {
    .quote-price {
      font-size: 1.8rem;
    }
    .quote-change,
    .quote-change-percent {
      font-size: 1.3rem;
      line-height: 1.3rem;
    }
  }
  @media screen and (max-width: 800px) {
    .quote-price-data {
      margin-bottom: 0.15rem;
      flex-direction: unset;
    }
  }
`;
